import {
  createMutationStore,
  createQueryStore,
} from '@ferreri/minato-react-utils';
import { useRouter } from 'next/router';

import { usePersistedGeolocation } from '@modules/geolocation/geolocation.store';
import { getCombinedConfig } from '@modules/publications/publications.store';
import { formatAddress } from '../../utils/formatAddress';
import { useCompanyIdQueryParam } from '../companies/companies.store';
import { useEstablishmentImage } from '../establishment-images/establishment-images.store';
import { COMPANY_ESTABLISHMENT_IMAGE } from '../routes/constants';
import {
  getCompaniesEstablishments,
  getCompanyEstablishment,
  getEstablishments,
  postCompaniesEstablishments,
  putCompaniesEstablishments,
} from './establishments.client';

export const useEstablishmentIdQueryParam = () => {
  const router = useRouter();

  return router?.query?.establishment_id as string;
};

export const useEstablishmentHourId = () => {
  const router = useRouter();

  return router?.query?.hour_id as string;
};

export const useEstablishmentsList = (
  params?: { latitude: number; longitude: number },
  config = {},
) => {
  return createQueryStore({
    queryKey: ['establishments_list', params?.latitude, params?.longitude],
    queryFn: () => getEstablishments(params),
    ...config,
  });
};

export const useCompaniesEstablishments = (config = {}) => {
  const companyId = useCompanyIdQueryParam();
  return createQueryStore({
    queryKey: ['establishments', companyId],
    queryFn: () => getCompaniesEstablishments(companyId),
    enabled: !!companyId,
    ...config,
  });
};

export const useCompanyEstablishment = (establishmentId, config = {}) => {
  const companyId = useCompanyIdQueryParam();
  return createQueryStore({
    queryKey: ['establishment', establishmentId, 'company', companyId],
    queryFn: () => getCompanyEstablishment(companyId, establishmentId),
    enabled: !!establishmentId && !!companyId,
    ...config,
  });
};

export const useCreateCompaniesEstablishments = (config = {}) => {
  const router = useRouter();

  return createMutationStore({
    mutationFn: (establishment) => postCompaniesEstablishments(establishment),
    ...getCombinedConfig({
      onSuccess: (establishment) => {
        router?.replace(
          COMPANY_ESTABLISHMENT_IMAGE(
            establishment?.company_id,
            establishment?.id,
          ),
        );
      },
      ...config,
    }),
  });
};

export const useUpdateCompaniesEstablishments = (config = {}) => {
  return createMutationStore({
    mutationFn: (establishment) => putCompaniesEstablishments(establishment),
    ...getCombinedConfig({
      onSuccess: () => history.back(),
      ...config,
    }),
  });
};

export const useCurrentEstablishment = (establishmentId, config = {}) => {
  return useCompanyEstablishment(establishmentId, config);
};

export const useCurrentEstablishmentImage = (establishmentId) => {
  const { isError, ...query } = useEstablishmentImage(establishmentId, {
    enabled: !!establishmentId,
    onError: () => null,
  });

  if (isError || !query?.data?.asset_url) {
    return { data: null, isLoading: false, isSuccess: false };
  }

  return query;
};

export const useEstablishmentHours = (establishment) => {
  return establishment?.hours;
};

export const useEstablishmentContacts = (establishment) => {
  const contacts = {
    address: formatAddress(establishment),
    whatsapp: establishment?.whatsapp,
    facebook: establishment?.facebook,
    instagram: establishment?.instagram,
    phone: establishment?.phone,
  };

  return Object.entries(contacts);
};

export const useEstablishmentsListComposeGeolocation = () => {
  const { value } = usePersistedGeolocation();
  const { data: result, isLoading: isEstablimentsListLoading } =
    useEstablishmentsList({
      latitude: value?.latitude,
      longitude: value?.longitude,
    });

  return { data: result, isLoading: isEstablimentsListLoading };
};
